import { readCopyFlag } from '@/lib/copy-search';
import { type NpcSearch, readNpcFilters } from '@/lib/npc-filters';

/**
 * What the three NPC addresses keep in the URL (npc.md §12), read by `validateSearch` and so
 * NEVER throwing: a link pasted with a typo falls back to the neutral screen, never to a blank
 * one (the régime of `lib/protocol-search.ts`).
 *
 *   - `/admin/npc` - the tab (`?onglet=bancs|series`, absent for « NPC »), the bench being edited
 *     (`?banc=<id>` or `?banc=nouveau`), and the filters of the NPC tab, sparse (`versioned-filters`);
 *   - `/admin/npc/<id>` - the tab of the fiche (`?onglet=rejeux`, absent for « Fiche »), the version
 *     read (`?version=`) and the flag of a copy (`?copie`);
 *   - `/admin/npc/nouveau` - `?depuis=<sessionId>`, the session the draft is prefilled from (§4.2).
 *
 * The tab and the bench are what a team thread pastes: « regarde les rejeux de Marie » and « ce
 * banc » must land where they were read.
 */

export const NPC_BENCH_NEW = 'nouveau';

export type NpcLibraryTab = 'npc' | 'bancs' | 'series';

export type NpcLibrarySearch = NpcSearch & {
  onglet?: 'bancs' | 'series';
  banc?: string;
};

export function readNpcLibrarySearch(search: Record<string, unknown>): NpcLibrarySearch {
  const result: NpcLibrarySearch = { ...readNpcFilters(search) };
  // OMITTED and never `undefined`: TanStack writes every key it is handed into the address bar.
  if (search.onglet === 'bancs' || search.onglet === 'series') result.onglet = search.onglet;
  if (typeof search.banc === 'string' && search.banc !== '') result.banc = search.banc;
  return result;
}

/** The tab a library search names. */
export function libraryTabOf(search: Pick<NpcLibrarySearch, 'onglet'>): NpcLibraryTab {
  return search.onglet ?? 'npc';
}

export type NpcFicheTab = 'fiche' | 'rejeux';

export type NpcFicheSearch = { version?: string; copie?: true; onglet?: 'rejeux' };

export function readNpcFicheSearch(search: Record<string, unknown>): NpcFicheSearch {
  const result: NpcFicheSearch = {};
  if (typeof search.version === 'string' && search.version !== '') result.version = search.version;
  const copie = readCopyFlag(search.copie);
  if (copie !== undefined) result.copie = copie;
  if (search.onglet === 'rejeux') result.onglet = 'rejeux';
  return result;
}

export function ficheTabOf(search: Pick<NpcFicheSearch, 'onglet'>): NpcFicheTab {
  return search.onglet ?? 'fiche';
}

export type NpcNewSearch = { depuis?: string };

export function readNpcNewSearch(search: Record<string, unknown>): NpcNewSearch {
  return typeof search.depuis === 'string' && search.depuis !== '' ? { depuis: search.depuis } : {};
}
